<template>
  <ontos-form-panel v-bind="heading" :loaded="loaded" :saving="isSaving"
                    :save-disabled="false" @save="internalSave">
    <!-- Slot com ESCOPO, como no InstanceEdit: os controles são da janela, mas o `dirty` nasce
         aqui, do diff contra o ponto de partida. -->
    <template v-if="$slots.controls" #controls>
      <!-- O rascunho vai no escopo junto do `dirty`: quem oferece a saída para a página
           inteira precisa dele para levá-lo, e sem isso Expandir recomeçaria do zero. -->
      <slot name="controls" :dirty="hasChanges" :draft="instance"/>
    </template>
    <!-- Na página de criar: grava e recomeça o formulário, para o próximo item -->
    <template v-if="createNext" #more-actions>
      <v-btn size="small" variant="text" :disabled="isSaving" @click="saveAndCreateNext">
        <v-icon size="small" start>mdi-content-save-move</v-icon>
        {{ $tt('save-create-next') }}
      </v-btn>
    </template>

    <v-form ref="form">
      <ontos-instance-input ref="input" :idea="idea" :instance="instance" :domains="domains"
                            :choice-domains="(idea && idea._domains) || []" :context="context"
                            :reverse-info="reverseInfo" :omit-properties="omitProperties"
                            :fixed-values="fixedValues || {}" :original="originalInstance"
                            @ready="autofocus && focusFirstField()"
                            @showMessage="$emit('showMessage', $event)"/>
    </v-form>
  </ontos-form-panel>
</template>
<script>
import {defineAsyncComponent} from "vue";
import LoadableComponent from "@/components/shared/LoadableComponent";
import FormPanel from "@/components/shared/FormPanel.vue";
import EditInstanceService from "@/services/EditInstanceService";
import {deepCopy} from "@/utils/copy";
import {IDENTIFIER_SEPARATOR, seedDefaults, seedState} from "@/utils/ontosUtils";
import {mediaAliasesOf} from "@/services/mediaStorage";
import {instanceCreate} from "@/utils/heading";
import {previewFromCreated} from "@/utils/instancePreview";
import {readForCreate} from "@/utils/instanceCreation";

/**
 * A criação de uma instância: o formulário, o que já vem semeado e a gravação. **Sem janela em
 * volta** — é o par do `InstanceEdit`, pelo mesmo motivo.
 *
 * Duas janelas o hospedam, e elas diferem no que fazem DEPOIS de salvar, que é a única coisa
 * que as distingue: a janela de uma instância troca para a ficha do item criado, e o diálogo
 * auxiliar — o "+" de um campo de relacionamento — devolve o item a quem o abriu, para ser
 * vinculado ao campo. Ir para a ficha ali perderia o formulário em preenchimento.
 */
export default {
  emits: ['saved', 'pending', 'showMessage', 'showErrorMessage'],
  extends: LoadableComponent,
  props: {
    context: {type: Object, required: true},
    initialName: String,
    //valores já preenchidos na nova instância (ex.: o dono de um relacionamento
    //reverso, que costuma ser obrigatório e sem o qual o servidor recusaria)
    initialValues: Object,
    //Não persiste: valida e devolve os dados para quem chamou criar depois. Serve
    //para o outro lado de um reverso cujo dono ainda não existe.
    deferred: {type: Boolean, default: false},
    //aliases escondidos do formulário e da validação
    omitProperties: {type: Array, default: () => []},
    /**
     * Valores que o item NASCE com e que não podem sair: o dono, quando se cria pelo "Adicionar" da
     * seção de um relacionamento na ficha dele (pedido do usuário: "nasce preenchido sem poder
     * remover"). Também são valores iniciais — não precisam ser repetidos no `initialValues`.
     *
     * Num campo de valor único o campo inteiro fica travado; num múltiplo, só o que está aqui — os
     * outros donos continuam podendo ser acrescentados. Quem trava é o `InstanceInput`.
     */
    fixedValues: {type: Object, default: null},
    /**
     * Tudo o que este formulário leria, quando quem abriu já tem: `{idea, domains, reverseInfo}`.
     *
     * Recebendo-o, **não há requisição nenhuma** — o modal abre instantâneo. O quadro, por
     * exemplo, já leu os candidatos para montar as raias, e a coleção com fluxo e domínios ele
     * monta do ambiente que já está carregado (`resolvedIdeaWith`).
     *
     * Um objeto, e não três props: é a MESMA forma que a leitura devolve, e é isso que faz não
     * existirem dois caminhos daqui para baixo. Em pedaços, faltar um deixaria metade do
     * formulário sem saber de nada — e calado.
     */
    known: {type: Object, default: null},
    /** Oferece "Salvar e criar outro" — a página de criar, onde se cadastra um item atrás do outro. */
    createNext: {type: Boolean, default: false},
    /** Põe o cursor no primeiro campo ao abrir — a página de criar, aberta para isso. */
    autofocus: {type: Boolean, default: false}
  },

  //Assíncrono para quebrar o ciclo: o InstanceInput abre um destes para criar o outro lado
  //de um relacionamento.
  components: {
    'ontos-form-panel': FormPanel,
    'ontos-instance-input': defineAsyncComponent(() => import('./InstanceInput.vue'))
  },

  data: function () {
    return {
      instance: null,
      idea: null,
      originalInstance: {},
      domains: {},
      reverseInfo: null,
      service: undefined
    }
  },

  computed: {
    //Só depois de carregar: a idea vem do servidor e o cabeçalho é montado com ela
    heading() {
      return this.idea ? instanceCreate(this.idea) : {title: '', subtitle: ''}
    },

    /** Enquanto não carregou não há service, e nada foi digitado ainda */
    hasChanges() {
      return !!this.service
          && Object.entries(this.service.instanceDiff(this.originalInstance, this.instance)).length > 0
    }
  },

  methods: {
    async load() {
      //Uma forma só, venha de onde vier — e quando vem de fora, nenhuma ida ao servidor.
      const {idea, domains, reverseInfo} =
          this.known || await readForCreate(this.$ideas, this.context.system, this.context.idea)
      this.idea = idea
      //Antes filtrava reversos, deixando o picker reverso sem itens
      this.domains = domains
      this.reverseInfo = reverseInfo
      this.service = new EditInstanceService(this.context, this.$instances, this.idea)
      this.startInstance(this.initialValues, this.initialName)
    },

    /**
     * O item que nasce: o nome e os valores dados, e a situação e os defaults semeados — tudo isso
     * é ponto de partida, e não rascunho. Na abertura e no "Salvar e criar outro", que recomeça
     * sem o que foi dado na abertura: o próximo item é outro.
     */
    startInstance(values, name) {
      this.instance = this.$ontos.consistantInstance({}, this.idea)

      if (name) {
        const identifier = this.idea.properties.find(p => p.identifier && p.type === 'text')
        if (identifier) this.instance[identifier.alias] = name
      }

      //Os fixos entram sempre, também no "Salvar e criar outro": o próximo item é outro, mas o dono
      //continua o mesmo
      const given = {...(values || {}), ...(this.fixedValues || {})}
      for (const alias in given) {
        this.instance[alias] = given[alias]
      }

      //Antes do instantâneo, e não no created() do InstanceInput: semear depois
      //faria o diálogo nascer sujo e pedir confirmação para fechar sem nada digitado
      seedState(this.instance, this.idea)
      seedDefaults(this.instance, this.idea, this.idea._domains, {candidates: this.domains})

      //Depois de semear: o que já vem preenchido é ponto de partida, não rascunho
      this.originalInstance = deepCopy(this.instance)
    },

    /**
     * O primeiro campo do formulário. `elements` é a coleção de controles do <form>, na ordem do
     * documento. Veio da página de criar, que o fazia por conta própria.
     *
     * Quando os campos aparecem (`ready`), e não quando o painel carrega: o item novo com campo de
     * acesso por fórmula ainda espera a prévia ali — ver o `InstanceInput.start`. Também no "Salvar e
     * criar outro", que recomeça o item e a espera.
     */
    async focusFirstField() {
      await this.$nextTick()
      const form = this.$refs.form && this.$refs.form.$el
      const first = form && form.elements && form.elements[0]
      if (first instanceof HTMLElement) first.focus()
    },

    async saveAndCreateNext() {
      await this.saveAnd(async () => {
        this.$refs.form.resetValidation()
        this.startInstance()
      })
    },

    showMessage() {
      this.info(this.$tt('info.saved'))
    },

    async internalSave() {
      await this.saveAnd(async added => {
        //Com a prévia da ficha do item novo: o rascunho com o id e o identificador que o servidor deu
        this.$emit('saved', added, previewFromCreated(added, this.instance, this.idea, this.domains, this.reverseInfo))
        this.$refs.form.resetValidation()
      })
    },

    /**
     * Identificador de uma instância que ainda não existe. Mesma composição que o
     * servidor faz: campos-chave na ordem, e relacionamento resolvido pelo texto do
     * domínio, não pelo id. O campo dono está em omitProperties e fica de fora, que é
     * justamente a parte que se repetiria em toda a lista.
     */
    pendingIdentifier() {
      const parts = this.idea.properties
          .filter(p => p.identifier && !this.omitProperties.includes(p.alias))
          .map(p => {
            const value = this.instance[p.alias]
            if (!p.relationship) return value
            const id = Number(Array.isArray(value) ? value[0] : value)
            const found = (this.relDomains[p.relationship.idea] || []).find(d => Number(d._instance) === id)
            return found ? found._identifier : undefined
          })
          .filter(v => v !== null && v !== undefined && v !== '')
      return parts.join(IDENTIFIER_SEPARATOR)
    },

    async saveAnd(next) {
      await this.saving(async () => {
        const validForm = await this.$refs.input.validateForm()
        if (validForm && this.deferred) {
          //nada vai para o servidor ainda: quem abriu guarda e cria no momento certo
          this.$emit('pending', {
            data: {...this.instance},
            identifier: this.pendingIdentifier(),
            //só este formulário carregou a idea: quem cria depois não saberia
            //quais campos são mídia nem que eles precisam subir para o Storage
            mediaAliases: mediaAliasesOf(this.idea)
          })
          return
        }
        if (!validForm) return
        let added
        try {
          added = await this.service.create(this.instance, this.$refs.input.pendingReverse())
        } catch (err) {
          //Não tratava recusa nenhuma: um 400 virava "Erro inesperado". As falhas de campo vão
          //para os campos; o resto, para a janela de recusa — ver `refuse`
          this.$refs.input.refuse(err)
          return
        }
        this.showMessage()
        if (next) next(added)
      })
    }
  }
}
</script>
